import axios from "axios";
import {
  GET_JOB_PROFILE_DETAILS_URL,
  LIST_JOB_ADVERTS_URL,
  UPDATE_ADVERT_URL,
} from "@/utils/constants/constants.js";
import viewAdvertModalHelpersMixin from "./view_advert_modal_helpers_mixin";
import viewAdvertModalUpdateMixin from "./view_advert_modal_update_mixin";

export default {
  name: "ViewAdvertModal",
  mixins: [viewAdvertModalHelpersMixin, viewAdvertModalUpdateMixin],
  emits: ["copy-advert-link", "view-applicants", "advert-updated", "advert-error"],
  props: {
    headers: { type: Object, required: true },
    selectedAdvert: { type: Object, default: null },
    openNonce: { type: Number, default: 0 },
  },
  data() {
    return {
      viewAdvertData: this.buildViewAdvertData(),
      profileSections: [],
      editMode: false,
      dragDisplayIndex: null,
      activeRichEditor: null,
      advertSaving: false,
      editAdvertData: this.buildEmptyEditData(),
    };
  },
  watch: {
    openNonce() {
      this.initializeViewAdvertModal();
    },
  },
  methods: {
    async initializeViewAdvertModal() {
      const advert = await this.fetchLatestAdvertForView();
      this.editMode = false;
      this.activeRichEditor = null;
      this.advertSaving = false;
      if (!advert?.id) {
        this.viewAdvertData = this.buildViewAdvertData();
        this.profileSections = [];
        this.editAdvertData = this.buildEmptyEditData();
        return;
      }

      const split = this.splitAdvertSectionsAndOrder(advert.sections || []);
      this.viewAdvertData = {
        ...this.buildViewAdvertData(),
        id: advert.id,
        title: advert.title || "",
        reference_number: advert.reference_number || "",
        published_date: advert.published_date || "",
        closing_date: advert.closing_date || "",
        vacancies: advert.vacancies || null,
        details: advert.details || "",
        sections: (split.contentSections || []).map((section) => this.normalizeAdvertSection(section)),
        display_order: this.normalizeDisplayOrder(
          split.orderItems,
          this.advertHasDetails(advert),
          split.contentSections
        ),
      };
      this.editAdvertData = this.buildEditDataFromAdvert(advert, split);

      await axios.get(GET_JOB_PROFILE_DETAILS_URL, {
        headers: this.headers,
        withCredentials: true,
        params: { entity_type_id: advert.entity_type_id },
      }).then((response) => {
        this.applyJobProfilePayload(response.data);
      }).catch(() => {
        this.profileSections = [];
      });

      this.viewAdvertData.display_blocks = this.buildDisplayBlocks(this.viewAdvertData);
    },
    async fetchLatestAdvertForView() {
      const selectedAdvert = this.selectedAdvert;
      if (!this.canFetchLatestAdvert(selectedAdvert)) {
        return selectedAdvert;
      }
      const advertId = Number(selectedAdvert?.id);
      const entityTypeId = Number(selectedAdvert?.entity_type_id);
      return axios.get(LIST_JOB_ADVERTS_URL, {
        headers: this.headers,
        withCredentials: true,
        params: { entity_type_id: entityTypeId },
      }).then((response) => {
        const latestAdvert = this.findLatestAdvert(response?.data?.data || [], advertId);
        return latestAdvert ? { ...selectedAdvert, ...latestAdvert } : selectedAdvert;
      }).catch(() => selectedAdvert);
    },
    hideViewModal() {
      const viewModal = document.getElementById("view_advert_modal");
      const bootstrapModal = window.bootstrap?.Modal;
      if (!viewModal || !bootstrapModal) return;
      const instance = bootstrapModal.getInstance(viewModal) || bootstrapModal.getOrCreateInstance(viewModal);
      instance.hide();
    },
    enterEditMode() {
      if (!this.selectedAdvert?.id) return;
      this.editMode = true;
    },
    exitEditMode() {
      this.editMode = false;
      this.activeRichEditor = null;
      const advert = this.selectedAdvert;
      if (!advert?.id) {
        this.editAdvertData = this.buildEmptyEditData();
        return;
      }
      const split = this.splitAdvertSectionsAndOrder(advert.sections || []);
      this.editAdvertData = this.buildEditDataFromAdvert(advert, split);
      this.editAdvertData.job_profile_title = this.viewAdvertData.job_profile_title;
      this.editAdvertData.job_profile_description = this.viewAdvertData.job_profile_description;
      this.editAdvertData.job_profile_sections = this.profileSections;
    },
    emitCopyAdvertLink() {
      if (!this.selectedAdvert?.id) return;
      this.$emit("copy-advert-link", this.selectedAdvert);
    },
    emitViewApplicants() {
      if (!this.selectedAdvert?.id) return;
      this.hideViewModal();
      this.$emit("view-applicants", this.selectedAdvert);
    },
    addAdvertSection() {
      if (!Array.isArray(this.editAdvertData.sections)) {
        this.editAdvertData.sections = [];
      }
      this.editAdvertData.sections.push({
        title: "",
        type: "text",
        content: "",
        table: { headers: [], rows: [] },
      });
      this.rebuildEditDisplayOrder();
    },
    removeAdvertSection(index) {
      if (!Array.isArray(this.editAdvertData.sections)) return;
      if (index < 0 || index >= this.editAdvertData.sections.length) return;
      this.editAdvertData.sections.splice(index, 1);
      this.rebuildEditDisplayOrder();
    },
    onDisplayDragStart(index) {
      this.dragDisplayIndex = index;
    },
    onDisplayDragEnd() {
      this.dragDisplayIndex = null;
    },
    onDisplayDrop(targetIndex) {
      if (!Array.isArray(this.editAdvertData.display_order)) return;
      const sourceIndex = this.dragDisplayIndex;
      if (!this.hasValidDisplayDragTarget(sourceIndex, targetIndex)) {
        this.dragDisplayIndex = null;
        return;
      }
      const list = [...this.editAdvertData.display_order];
      const [moved] = list.splice(sourceIndex, 1);
      list.splice(targetIndex, 0, moved);
      this.editAdvertData.display_order = list;
      this.dragDisplayIndex = null;
    },
    async saveAdvertChanges() {
      if (this.activeRichEditor) {
        this.activeRichEditor.dispatchEvent(new Event("blur", { bubbles: true }));
      }
      this.rebuildEditDisplayOrder();
      if (!this.validateAdvertInputs()) return;

      this.advertSaving = true;
      await axios.post(UPDATE_ADVERT_URL, this.buildUpdateAdvertPayload(), {
        headers: this.headers,
        withCredentials: true,
      }).then((response) => {
        this.handleAdvertUpdateSuccess(response);
      }).catch((error) => {
        this.emitAdvertSaveError(error);
      }).finally(() => {
        this.advertSaving = false;
      });
    },
  },
};
